import {
  CANVAS_DESIGN_SCHEMA,
  type CanvasDesign,
} from "./canvas-design";

/**
 * Mirrors greeting-card/editor/card-draft-store.ts: versioned payload, 7-day
 * TTL, stale or malformed entries deleted on read, silent no-op when storage is
 * unavailable. Kept separate rather than genericising that file, which is typed
 * and validated for SlotDesign[] and works.
 *
 * Stores the WHOLE configuration, not just the crop — a reload has to bring
 * back the size and the photo too, or restoring the crop alone is worthless.
 */
export const CANVAS_DRAFT_VERSION = 1;
export const CANVAS_DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

export interface CanvasDraft {
  design: CanvasDesign;
  /** Retained for drafts written before the size-only flow. */
  axisValues: Record<string, string>;
  sourceKey: string | null;
  /** The chosen size. Null on drafts predating the size-only flow. */
  variantId?: string | null;
}

interface StoredDraft extends CanvasDraft {
  v: number;
  savedAt: number;
}

function looksLikeDesign(value: unknown): boolean {
  if (!value || typeof value !== "object") return false;
  const d = value as Partial<CanvasDesign>;
  return (
    d.schema === CANVAS_DESIGN_SCHEMA &&
    !!d.crop &&
    typeof d.crop === "object" &&
    typeof (d.crop as { width?: unknown }).width === "number" &&
    !!d.edge &&
    typeof (d.edge as { mode?: unknown }).mode === "string"
  );
}

export function writeCanvasDraft(
  key: string,
  draft: CanvasDraft,
  now: number,
): void {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    const payload: StoredDraft = {
      v: CANVAS_DRAFT_VERSION,
      savedAt: now,
      ...draft,
    };
    window.localStorage.setItem(key, JSON.stringify(payload));
  } catch {
    /* quota / private mode / serialization — a lost draft must not break the editor */
  }
}

export function readCanvasDraft(key: string, now: number): CanvasDraft | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<StoredDraft>;
    const ok =
      parsed &&
      parsed.v === CANVAS_DRAFT_VERSION &&
      typeof parsed.savedAt === "number" &&
      now - parsed.savedAt <= CANVAS_DRAFT_TTL_MS &&
      looksLikeDesign(parsed.design) &&
      !!parsed.axisValues &&
      typeof parsed.axisValues === "object";

    if (!ok) {
      clearCanvasDraft(key);
      return null;
    }

    return {
      design: parsed.design as CanvasDesign,
      axisValues: parsed.axisValues as Record<string, string>,
      sourceKey: parsed.sourceKey ?? null,
      variantId: parsed.variantId ?? null,
    };
  } catch {
    clearCanvasDraft(key);
    return null;
  }
}

export function clearCanvasDraft(key: string): void {
  try {
    if (typeof window === "undefined" || !window.localStorage) return;
    window.localStorage.removeItem(key);
  } catch {
    /* ignore */
  }
}
